import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { popularComparisons as comparisons, type PopularComparisonCard, type PopularComparisonImage } from '../../../../packages/shared/src/popular-comparisons.js';
import { comparisonPath } from '../../../../packages/shared/src/catalogue-seo.js';
import { useResource } from '../api.js';

function ComparisonImage({ image }: { image: PopularComparisonImage }) {
  return image ? (
    <img
      src={'/media/' + encodeURIComponent(image.id) + '?size=thumb&v=' + Date.parse(image.updatedAt)}
      alt=""
      loading="lazy"
      decoding="async"
      draggable={false}
    />
  ) : <span className="comparison-coverflow-image-missing">Photo coming soon</span>;
}

// Two interlaced scent rings: the right ring crosses over the left at the top and under it at the bottom.
// Each crossing redraws a short arc above a background-coloured knockout, which reads as the weave.
function OverlapMark() {
  return (
    <svg className="comparison-coverflow-overlap" viewBox="2 0 20 16" width="20" height="16" aria-hidden="true">
      <path className="comparison-coverflow-overlap-lens" d="M12 2.546A6 6 0 0 1 12 13.454A6 6 0 0 1 12 2.546Z" />
      <g className="comparison-coverflow-overlap-left"><circle cx="9.5" cy="8" r="6" /></g>
      <g className="comparison-coverflow-overlap-right">
        <circle cx="14.5" cy="8" r="6" />
        <path className="comparison-coverflow-overlap-knock" d="M9.931 4.111A6 6 0 0 1 14.542 2" />
        <path d="M9.931 4.111A6 6 0 0 1 14.542 2" />
      </g>
      <g className="comparison-coverflow-overlap-left">
        <path className="comparison-coverflow-overlap-knock" d="M14.069 11.889A6 6 0 0 1 9.458 14" />
        <path d="M14.069 11.889A6 6 0 0 1 9.458 14" />
      </g>
    </svg>
  );
}

export function PopularComparisonCoverflow({ initialCards }: { initialCards?: PopularComparisonCard[] }) {
  const navigate = useNavigate();
  // Keep server-rendered cards for the first paint, then refresh photos added since that HTML was loaded.
  const cards = useResource<PopularComparisonCard[]>('/api/v1/popular-comparisons').data ?? initialCards;
  const [active, setActive] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [physical, setPhysical] = useState(false);
  const [layoutVersion, setLayoutVersion] = useState(0);
  const visiblePosition = active - dragOffset;
  const displayedIndex = ((Math.round(visiblePosition) % comparisons.length) + comparisons.length) % comparisons.length;
  const visibleRange = 1;
  const sideTilt = 42;
  const pointer = useRef<{ startX: number; lastX: number; lastTime: number; velocity: number; baseOffset: number; slot: number | null } | null>(null);
  const springFrame = useRef<number | null>(null);
  const track = useRef<HTMLDivElement>(null);
  const cardAnimations = useRef(new Map<HTMLElement, Animation>());
  // The step is defined in CSS so the cards can span the section; this probe reads it back in pixels for drag maths.
  const stepProbe = useRef<HTMLSpanElement>(null);
  const coverflowStep = () => stepProbe.current?.offsetWidth || 210;
  useEffect(() => {
    if (!stepProbe.current || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => setLayoutVersion((version) => version + 1));
    observer.observe(stepProbe.current);
    return () => observer.disconnect();
  }, []);
  const move = (direction: number) => {
    if (springFrame.current !== null) cancelAnimationFrame(springFrame.current);
    springFrame.current = null;
    setDragOffset(0);
    setPhysical(false);
    setActive((current) => current + direction);
  };

  useLayoutEffect(() => {
    const elements = [...(track.current?.querySelectorAll<HTMLElement>('[data-coverflow-slot]') ?? [])];
    const present = new Set(elements);
    for (const [element, animation] of cardAnimations.current) {
      if (!present.has(element)) {
        animation.cancel();
        cardAnimations.current.delete(element);
      }
    }
    const step = coverflowStep();
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    for (const element of elements) {
      const slot = Number(element.dataset.coverflowSlot);
      const offset = slot - visiblePosition;
      const distance = Math.abs(offset);
      const tilt = Math.sign(offset) * Math.min(62, distance <= 1 ? distance * sideTilt : sideTilt + (distance - 1) * 9);
      const sideSpread = Math.sign(offset) * 12 * Math.max(0, distance * (2 - distance));
      const transform = `translateX(${offset * step + sideSpread}px) rotateY(${-tilt}deg) scale(${1 - distance * 0.12})`;
      const opacity = String(Math.min(1, Math.max(0, (visibleRange + 0.9 - distance) / 0.65)));
      const previous = cardAnimations.current.get(element);
      const computed = getComputedStyle(element);
      const from = { transform: computed.transform, opacity: computed.opacity };
      previous?.cancel();
      cardAnimations.current.set(element, element.animate([from, { transform, opacity }], {
        duration: !physical && previous && !reducedMotion ? 560 : 0,
        easing: 'cubic-bezier(.22,1,.36,1)',
        fill: 'forwards',
      }));
    }
  }, [visiblePosition, physical, layoutVersion]);

  useEffect(() => () => {
    if (springFrame.current !== null) cancelAnimationFrame(springFrame.current);
    for (const animation of cardAnimations.current.values()) animation.cancel();
    cardAnimations.current.clear();
  }, []);

  const settle = (initialPosition: number, initialVelocity: number) => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setDragOffset(0);
      setPhysical(false);
      return;
    }
    let position = initialPosition;
    let velocity = initialVelocity;
    let lastTime = performance.now();
    const frame = (time: number) => {
      const dt = Math.min((time - lastTime) / 1000, 0.032);
      lastTime = time;
      velocity += (-115 * position - 19 * velocity) * dt;
      position += velocity * dt;
      if (Math.abs(position) < 0.002 && Math.abs(velocity) < 0.02) {
        setDragOffset(0);
        setPhysical(false);
        springFrame.current = null;
        return;
      }
      setDragOffset(position);
      springFrame.current = requestAnimationFrame(frame);
    };
    springFrame.current = requestAnimationFrame(frame);
  };

  return (
    <section className={'comparison-coverflow' + (physical ? ' is-physical' : '')} aria-labelledby="comparison-coverflow-title">
      <div className="comparison-coverflow-heading">
        <div>
          <h2 id="comparison-coverflow-title">Side by side</h2>
          <p>Explore two scent profiles at a time, from shared accords to the details that differ.</p>
        </div>
        <div className="comparison-coverflow-controls">
          <button type="button" aria-label="Previous pair" onClick={() => move(-1)}>
            Previous
          </button>
          <span aria-live="polite">{displayedIndex + 1} / {comparisons.length}</span>
          <button type="button" aria-label="Next pair" onClick={() => move(1)}>
            Next
          </button>
        </div>
      </div>
      <div
        ref={track}
        className="comparison-coverflow-track"
        aria-label="Fragrances shown side by side"
        onPointerDown={(event) => {
          if (event.pointerType === 'mouse' && (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey)) return;
          if (event.pointerType === 'mouse') event.preventDefault();
          if (springFrame.current !== null) cancelAnimationFrame(springFrame.current);
          springFrame.current = null;
          const card = (event.target as HTMLElement).closest<HTMLElement>('[data-coverflow-slot]');
          pointer.current = { startX: event.clientX, lastX: event.clientX, lastTime: event.timeStamp, velocity: 0, baseOffset: dragOffset, slot: card ? Number(card.dataset.coverflowSlot) : null };
          event.currentTarget.setPointerCapture(event.pointerId);
          setPhysical(true);
        }}
        onPointerMove={(event) => {
          const current = pointer.current;
          if (!current) return;
          const distance = event.clientX - current.startX;
          const elapsed = Math.max(1, event.timeStamp - current.lastTime);
          current.velocity = current.velocity * 0.6 + ((event.clientX - current.lastX) / elapsed) * 0.4;
          current.lastX = event.clientX;
          current.lastTime = event.timeStamp;
          setDragOffset(current.baseOffset + distance / coverflowStep());
        }}
        onPointerUp={(event) => {
          const current = pointer.current;
          if (!current) return;
          pointer.current = null;
          const step = coverflowStep();
          const distance = event.clientX - current.startX;
          if (Math.abs(distance) <= 5) {
            setDragOffset(0);
            setPhysical(false);
            // A tap opens its card even while the carousel is settling from a swipe.
            if (current.slot !== null) {
              const comparison = comparisons[((current.slot % comparisons.length) + comparisons.length) % comparisons.length];
              navigate(comparisonPath(comparison.slug, comparison.alternativeSlug));
            }
            return;
          }
          const offset = current.baseOffset + distance / step;
          const velocity = event.timeStamp - current.lastTime > 90 ? 0 : current.velocity * 1000 / step;
          const momentum = Math.max(-0.4, Math.min(0.4, velocity * 0.18));
          const advance = Math.round(-(offset + momentum));
          setActive((index) => index + advance);
          const position = offset + advance;
          setDragOffset(position);
          settle(position, velocity);
        }}
        onPointerCancel={() => {
          const current = pointer.current;
          pointer.current = null;
          if (!current) return;
          const offset = current.baseOffset + (current.lastX - current.startX) / coverflowStep();
          const advance = Math.round(-offset);
          setActive((index) => index + advance);
          const position = offset + advance;
          setDragOffset(position);
          settle(position, 0);
        }}
      >
        <span ref={stepProbe} className="comparison-coverflow-step" aria-hidden="true" />
        {Array.from({ length: 9 }, (_, position) => Math.round(visiblePosition) + position - 4).map((slot) => {
          const { originalBrand, original, alternativeBrand, alternative, slug, alternativeSlug } = comparisons[((slot % comparisons.length) + comparisons.length) % comparisons.length];
          const offset = slot - visiblePosition;
          const distance = Math.abs(offset);
          const position = Math.round(offset);
          const images = cards?.find(card => card.slug === slug && card.alternativeSlug === alternativeSlug);
          const shared = images?.sharedAccords ?? [];
          return (
            <Link
              key={slot}
              data-coverflow-slot={slot}
              className={'comparison-coverflow-card position-' + position + (offset === 0 ? ' is-active' : '')}
              to={comparisonPath(slug, alternativeSlug)}
              draggable={false}
              aria-label={originalBrand + ' ' + original + ' and ' + alternativeBrand + ' ' + alternative + ', side by side' +
                (shared.length ? '. Accords in common: ' + shared.join(', ') : '')}
              aria-hidden={distance > visibleRange + 0.9}
              tabIndex={distance > visibleRange + 0.45 ? -1 : 0}
              onClick={(event) => {
                // Pointer clicks are handled on pointer up so drags never navigate; keyboard activation follows the link.
                if (event.detail !== 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey) event.preventDefault();
              }}
              onDragStart={(event) => event.preventDefault()}
            >
              <span className="comparison-coverflow-card-inner">
                <span className="comparison-coverflow-pair">
                  <span className="comparison-coverflow-product">
                    <span className="comparison-coverflow-product-heading"><span className="comparison-coverflow-brand">{originalBrand}</span><strong>{original}</strong></span>
                    <span className="comparison-coverflow-image"><ComparisonImage image={images?.originalImage ?? null} /></span>
                  </span>
                  <span className="comparison-coverflow-pair-divider" aria-hidden="true" />
                  <span className="comparison-coverflow-product">
                    <span className="comparison-coverflow-product-heading"><span className="comparison-coverflow-brand">{alternativeBrand}</span><strong>{alternative}</strong></span>
                    <span className="comparison-coverflow-image"><ComparisonImage image={images?.alternativeImage ?? null} /></span>
                  </span>
                </span>
                <span className="comparison-coverflow-footer">
                  <OverlapMark />
                  {shared.length ? (
                    <span className="comparison-coverflow-shared">
                      <b>In common</b>
                      {shared.map((accord) => <span key={accord} className="comparison-coverflow-accord">{accord}</span>)}
                    </span>
                  ) : <span className="comparison-coverflow-shared">See shared notes and accords</span>}
                </span>
              </span>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
